import React from "react"
import { useEditor } from "@agenteresolve/react"
import { Block } from "baseui/block"
import { useTranslation } from "react-i18next"
import { TimerContext } from "@layerhub-io/use-timer"
import { loadFonts } from "~/utils/fonts"
import Scrollable from "~/components/Scrollable"
import AngleDoubleLeft from "~/components/Icons/AngleDoubleLeft"
import { useStyletron } from "baseui"
import { ANIMATED_TEMPLATES } from "~/constants/animatedTemplates"
import useSetIsSidebarOpen from "~/hooks/useSetIsSidebarOpen"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import { loadVideoEditorAssets } from "~/utils/video"
import { renderScenePreview } from "~/utils/previews"
import { fetchTemplateThumbs, uploadTemplateThumb } from "~/utils/thumbnails"
import { checkUsage, reportUsage, USAGE_AGENT_GENERATION } from "~/utils/saas"
import { hfApiUrl } from "~/utils/hyperframes"
import { CONTENT_KINDS, ContentKind, KIND_ORDER } from "~/constants/content-kinds"

const USER_INDEX_URL = "/user-templates/index.json"
const USER_TEMPLATE_URL = (id: string) => `/user-templates/t/${id}.json`
const PAGE_SIZE = 60
const ACTIVE_TEMPLATE_KEY = "tpl-active-id"
const THUMB_KEY = (id: string) => `tpl-thumb:${id}`

const agentSelectStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.3rem 0.4rem",
  borderRadius: 8,
  border: "1px solid #e2e2ea",
  fontSize: "0.7rem",
  background: "#fff",
  color: "#374151",
}

export default function () {
  const editor = useEditor()
  const { t } = useTranslation("editor")
  const setIsSidebarOpen = useSetIsSidebarOpen()
  const { setCurrentScene, setScenes, scenes, currentScene, currentDesign, contentKind, setActiveFormat } = useDesignEditorContext()
  const kindSpec = CONTENT_KINDS[contentKind]
  const { setTime } = React.useContext(TimerContext) as any
  const previewTaskRef = React.useRef(0)
  const [userTemplates, setUserTemplates] = React.useState<any[] | null>(null)
  const [query, setQuery] = React.useState("")
  const [category, setCategory] = React.useState("all")
  const [kindFilter, setKindFilter] = React.useState<"ALL" | ContentKind>(contentKind)
  const [visible, setVisible] = React.useState(PAGE_SIZE)
  const [thumbVersion, setThumbVersion] = React.useState(0)
  const [snapshotting, setSnapshotting] = React.useState(false)
  const [prompt, setPrompt] = React.useState("")
  const [generating, setGenerating] = React.useState(false)
  const [agentInfo, setAgentInfo] = React.useState("")
  const [reviseText, setReviseText] = React.useState("")
  const [revising, setRevising] = React.useState(false)
  const [reviseInfo, setReviseInfo] = React.useState("")
  const [agentFormat, setAgentFormat] = React.useState("auto")
  const [agentDuration, setAgentDuration] = React.useState(5000)
  const [agentStyle, setAgentStyle] = React.useState("auto")
  const [useCurrentTemplate, setUseCurrentTemplate] = React.useState(false)
  const [serverThumbs, setServerThumbs] = React.useState<Record<string, string>>({})

  const animatedEntries = React.useMemo(
    () =>
      ANIMATED_TEMPLATES.map((template: any, index: number) => ({
        id: template.id || `animated-${index}`,
        name: template.name || `Animated ${index + 1}`,
        catName: "Animated",
        thumb: template.preview,
        animated: template,
      })),
    []
  )

  // Timeline thumbnails: render each scene once in the background so every
  // timeline item shows the scene it represents (multi-scene templates have no
  // stored previews). Cancelled when another template is loaded meanwhile.
  const fillScenePreviews = React.useCallback(
    async (list: any[]) => {
      if (!editor) return
      const task = ++previewTaskRef.current
      for (const scene of list) {
        if (task !== previewTaskRef.current) return
        try {
          const preview = await renderScenePreview(editor, scene)
          if (task !== previewTaskRef.current) return
          if (preview) {
            setScenes((prev: any[]) => prev.map((p: any) => (p.id === scene.id ? { ...p, preview } : p)))
          }
        } catch (err) {
          console.warn("scene preview failed", scene?.id, err)
        }
      }
    },
    [editor, setScenes]
  )

  const loadTemplate = React.useCallback(
    async (template: any) => {
      if (editor) {
        setActiveFormat(null)
        const scenes = Array.isArray(template.scenes) && template.scenes.length ? template.scenes : [template]
        const fonts: any[] = []
        scenes.forEach((scene: any) => {
          ;(scene.layers || []).forEach((object: any) => {
            if (object.type === "StaticText" || object.type === "DynamicText") {
              fonts.push({
                name: object.fontFamily,
                url: object.fontURL,
                options: { style: object.fontStyle || "normal", weight: object.fontWeight || 400 },
              })
            }
          })
        })
        const filteredFonts = fonts.filter((f) => !!f.url)
        if (filteredFonts.length > 0) {
          await loadFonts(filteredFonts)
        }

        if (Array.isArray(template.scenes) && template.scenes.length) {
          // Multi-scene (video) template: warm up video layers per scene.
          const prepared: any[] = []
          for (let index = 0; index < template.scenes.length; index += 1) {
            const scene = { ...template.scenes[index], id: template.scenes[index].id || `${template.id}-scene-${index}` }
            prepared.push(await loadVideoEditorAssets(scene))
          }
          setScenes(prepared)
          setCurrentScene(prepared[0])
          if (typeof setTime === "function") setTime(0)
          fillScenePreviews(prepared)
        } else {
          // Single-scene template: replace the timeline entirely (the previous
          // template's scenes must not linger).
          const scene = { ...template, id: currentScene?.id || template.id }
          setScenes([scene])
          setCurrentScene(scene)
          if (typeof setTime === "function") setTime(0)
          fillScenePreviews([scene])
        }
      }
    },
    [editor, currentScene, setScenes, setTime, fillScenePreviews, setActiveFormat]
  )

  React.useEffect(() => {
    // no-store: the index is regenerated by the import script; avoid serving a
    // stale cached copy after a new deploy.
    fetch(USER_INDEX_URL, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setUserTemplates(Array.isArray(data) ? data : []))
      .catch(() => setUserTemplates([]))
  }, [])

  // Server-side snapshot overrides (survive across browsers/machines).
  const refreshServerThumbs = React.useCallback(() => {
    fetchTemplateThumbs().then(setServerThumbs)
  }, [])

  React.useEffect(() => {
    refreshServerThumbs()
  }, [refreshServerThumbs])

  // Refresh thumbnails when a snapshot is saved anywhere (e.g. the Navbar).
  React.useEffect(() => {
    const onThumbUpdated = () => {
      setThumbVersion((v) => v + 1)
      refreshServerThumbs()
    }
    window.addEventListener("tpl-thumb-updated", onThumbUpdated)
    return () => window.removeEventListener("tpl-thumb-updated", onThumbUpdated)
  }, [refreshServerThumbs])

  const allTemplates = React.useMemo(() => {
    return [...animatedEntries, ...(userTemplates || [])]
  }, [animatedEntries, userTemplates])

  const kindOfTemplate = React.useCallback((entry: any): ContentKind => {
    return KIND_ORDER.includes(entry?.kind) ? entry.kind : entry?.animated ? "VIDEO" : "DIGITAL"
  }, [])

  React.useEffect(() => {
    setKindFilter(contentKind)
  }, [contentKind])

  const categories = React.useMemo(() => {
    const counts = new Map<string, number>()
    for (const t of allTemplates) {
      const label = t.catName || "Other"
      counts.set(label, (counts.get(label) || 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ label, count }))
  }, [allTemplates])

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    return allTemplates.filter((t) => {
      if (kindFilter !== "ALL" && kindOfTemplate(t) !== kindFilter) return false
      if (category !== "all" && (t.catName || "Other") !== category) return false
      if (q && !String(t.name || "").toLowerCase().includes(q)) return false
      return true
    })
  }, [allTemplates, query, category, kindFilter, kindOfTemplate])

  const thumbOf = React.useCallback(
    (entry: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const _version = thumbVersion
      try {
        const stored = localStorage.getItem(THUMB_KEY(entry.id))
        if (stored) return stored
      } catch {
        // ignore storage errors
      }
      if (serverThumbs[entry.id]) return serverThumbs[entry.id]
      return entry.thumb
    },
    [thumbVersion, serverThumbs]
  )

  // Fase 3 (v1): prompt -> template preenchido pelo serviço (determinístico).
  const generateFromPrompt = React.useCallback(async () => {
    const text = prompt.trim()
    if (!text || generating) return
    setGenerating(true)
    setAgentInfo("")
    setAgentInfo(t("agent.consultingModel"))
    try {
      const quota = await checkUsage(USAGE_AGENT_GENERATION)
      if (quota && !quota.allowed) {
        setAgentInfo(t("agent.limitReached"))
        return
      }
      let activeTemplateId = ""
      if (useCurrentTemplate) {
        try {
          activeTemplateId = localStorage.getItem(ACTIVE_TEMPLATE_KEY) || ""
        } catch {
          // ignore storage errors
        }
      }
      // SSE de estágios: llm:start -> plan:done -> compose:start -> done.
      const res = await fetch(hfApiUrl("/agent/generate/stream"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: text,
          kind: contentKind,
          animated: kindSpec.animated,
          format: agentFormat === "auto" ? undefined : agentFormat,
          style: agentStyle === "auto" ? undefined : agentStyle,
          durationMs: kindSpec.animated ? agentDuration : undefined,
          templateId: activeTemplateId || undefined,
        }),
      })
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""
      let finalPayload: any = null
      let streamError = ""
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const parts = buffer.split("\n\n")
        buffer = parts.pop() || ""
        for (const part of parts) {
          const line = part.split("\n").find((entry) => entry.startsWith("data: "))
          if (!line) continue
          let event: any
          try {
            event = JSON.parse(line.slice(6))
          } catch {
            continue
          }
          if (event.stage === "llm:start") setAgentInfo(t("agent.consultingModel"))
          else if (event.stage === "plan:done") {
            const provider = event.metrics?.provider
            const template = event.template?.name || "template"
            setAgentInfo(
              provider
                ? t("agent.planFromModel", { provider, template })
                : t("agent.planLocal", { template })
            )
          } else if (event.stage === "compose:start") setAgentInfo(t("agent.composing"))
          else if (event.stage === "done") finalPayload = event.payload
          else if (event.stage === "error") streamError = event.error || "agent failed"
        }
      }
      if (streamError) throw new Error(streamError)
      if (!finalPayload) throw new Error("stream finished without result")
      await loadTemplate(finalPayload.editorDesign)
      setAgentInfo(t("agent.generatedWith", { name: finalPayload.template?.name || "template" }))
      reportUsage(USAGE_AGENT_GENERATION, {
        template_id: finalPayload.template?.id,
        strategy: finalPayload.plan?.strategy,
      })
    } catch (err: any) {
      setAgentInfo(t("agent.failed", { error: String(err?.message || err) }))
    } finally {
      setGenerating(false)
    }
  }, [prompt, generating, loadTemplate, agentFormat, agentStyle, agentDuration, useCurrentTemplate, t, contentKind, kindSpec])

  // Ajuste do design atual em linguagem natural (POST /agent/revise).
  const reviseCurrentDesign = React.useCallback(async () => {
    const instruction = reviseText.trim()
    if (!instruction || revising || !editor) return
    setRevising(true)
    setReviseInfo("")
    try {
      const current = editor.scene.exportToJSON() as any
      const list: any[] = scenes && scenes.length ? scenes : [current]
      const design = {
        id: current?.id || "design",
        name: current?.name || "Untitled Design",
        frame: current?.frame,
        scenes: list.map((page: any) => ({
          id: page.id,
          name: page.name || "",
          duration: page.duration ?? 5000,
          layers: page.id === current?.id ? current.layers : page.layers,
        })),
        metadata: {},
      }
      const res = await fetch(hfApiUrl("/agent/revise"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ design, instruction, kind: contentKind, animated: kindSpec.animated }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || "revise failed")
      await loadTemplate(data.editorDesign)
      setReviseInfo((data.plan?.changes || []).join(" · ") || t("agent.reviseApplied"))
      setReviseText("")
    } catch (err: any) {
      setReviseInfo(t("agent.reviseFailed", { error: String(err?.message || err) }))
    } finally {
      setRevising(false)
    }
  }, [reviseText, revising, editor, scenes, loadTemplate, t, contentKind, kindSpec])

  const openTemplate = React.useCallback(
    async (entry: any) => {
      try {
        let template = entry.animated
        if (!template) {
          const res = await fetch(USER_TEMPLATE_URL(entry.id), { cache: "no-store" })
          if (!res.ok) return
          template = await res.json()
        }
        try {
          localStorage.setItem(ACTIVE_TEMPLATE_KEY, entry.id)
        } catch {
          // ignore
        }
        await loadTemplate(template)
      } catch (err) {
        console.error("failed to load template", err)
      }
    },
    [loadTemplate]
  )

  const saveSnapshot = React.useCallback(async () => {
    if (!editor) return
    setSnapshotting(true)
    try {
      const scene = editor.scene.exportToJSON()
      const preview = (await editor.renderer.render(scene)) as string
      if (!preview) return
      let activeId = ""
      try {
        activeId = localStorage.getItem(ACTIVE_TEMPLATE_KEY) || ""
      } catch {
        // ignore
      }
      if (!activeId) activeId = currentDesign?.id || scene.id || "design"
      try {
        localStorage.setItem(THUMB_KEY(activeId), preview)
      } catch {
        // ignore
      }
      await uploadTemplateThumb(activeId, preview)
      const a = document.createElement("a")
      a.href = preview
      a.download = `snapshot-${activeId}.png`
      a.click()
      setThumbVersion((v) => v + 1)
      refreshServerThumbs()
    } catch (err) {
      console.error("snapshot failed", err)
    } finally {
      setSnapshotting(false)
    }
  }, [editor, currentDesign, refreshServerThumbs])

  return (
    <Block $style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Block
        className="ar-panel-title"
        $style={{
          display: "flex",
          alignItems: "center",
          fontWeight: 500,
          justifyContent: "space-between",
          padding: "1.5rem",
          paddingBottom: "0.75rem",
        }}
      >
        <Block>{t("panels.panelsList.templates")}</Block>

        <Block onClick={() => setIsSidebarOpen(false)} $style={{ cursor: "pointer", display: "flex" }}>
          <AngleDoubleLeft size={18} />
        </Block>
      </Block>

      <Block className="ar-agent-card" $style={{ padding: "0 1.5rem 0.75rem" }}>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={t("agent.promptPlaceholder")}
          rows={2}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "0.5rem 0.75rem",
            borderRadius: "8px",
            border: "1px solid #d8dbe6",
            fontSize: "0.85rem",
            outline: "none",
            resize: "none",
            fontFamily: "inherit",
            background: "#fbfbfd",
          }}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: kindSpec.animated ? "0.85fr 0.62fr 1.13fr" : "1fr 1fr",
            gap: "0.4rem",
            marginTop: "0.4rem",
          }}
        >
          <select
            value={agentFormat}
            onChange={(e) => setAgentFormat(e.target.value)}
            style={agentSelectStyle}
            title={t("agent.formatTooltip")}
          >
            <option value="auto">{t("agent.formatAuto")}</option>
            <option value="landscape">{t("agent.formatLandscape")}</option>
            <option value="square">{t("agent.formatSquare")}</option>
            <option value="portrait">{t("agent.formatPortrait")}</option>
            <option value="banner">{t("agent.formatBanner")}</option>
          </select>
          {kindSpec.animated && (
            <select
              value={agentDuration}
              onChange={(e) => setAgentDuration(Number(e.target.value))}
              style={agentSelectStyle}
              title={t("agent.durationTooltip")}
            >
              <option value={5000}>5 s</option>
              <option value={10000}>10 s</option>
              <option value={15000}>15 s</option>
              <option value={30000}>30 s</option>
            </select>
          )}
          <select
            value={agentStyle}
            onChange={(e) => setAgentStyle(e.target.value)}
            style={agentSelectStyle}
            title={t("agent.styleTooltip")}
          >
            <option value="auto">{t("agent.styleAuto")}</option>
            <option value="impacto">{t("agent.styleImpact")}</option>
            <option value="elegante">{t("agent.styleElegant")}</option>
            <option value="divertido">{t("agent.styleFun")}</option>
            <option value="corporativo">{t("agent.styleCorporate")}</option>
          </select>
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.35rem",
            fontSize: "0.68rem",
            color: "#5b6484",
            marginTop: "0.35rem",
          }}
        >
          <input
            type="checkbox"
            checked={useCurrentTemplate}
            onChange={(e) => setUseCurrentTemplate(e.target.checked)}
          />
          {t("agent.useCurrentTemplate")}
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.4rem" }}>
          <button
            onClick={generateFromPrompt}
            disabled={generating || !prompt.trim()}
            style={{
              border: 0,
              borderRadius: 8,
              padding: "0.4rem 0.8rem",
              background: generating || !prompt.trim() ? "#b9c3d9" : "#192bc2",
              color: "#ffffff",
              fontSize: "0.78rem",
              fontWeight: 600,
              cursor: generating || !prompt.trim() ? "default" : "pointer",
            }}
          >
            {generating ? t("agent.generating") : t("agent.generate")}
          </button>
          {agentInfo && (
            <span style={{ fontSize: "0.7rem", color: agentInfo.startsWith("Falha") ? "#b3261e" : "#5b6484" }}>
              {agentInfo}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.4rem" }}>
          <input
            value={reviseText}
            onChange={(e) => setReviseText(e.target.value)}
            placeholder={t("agent.revisePlaceholder")}
            style={{
              flex: 1,
              boxSizing: "border-box",
              padding: "0.35rem 0.6rem",
              borderRadius: "8px",
              border: "1px solid #e2e2ea",
              fontSize: "0.75rem",
              outline: "none",
            }}
          />
          <button
            onClick={reviseCurrentDesign}
            disabled={revising || !reviseText.trim()}
            style={{
              border: 0,
              borderRadius: 8,
              padding: "0.35rem 0.7rem",
              background: revising || !reviseText.trim() ? "#c7cdd8" : "#4F46E5",
              color: "#ffffff",
              fontSize: "0.72rem",
              fontWeight: 600,
              cursor: revising || !reviseText.trim() ? "default" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {revising ? t("agent.applying") : t("agent.apply")}
          </button>
        </div>
        {reviseInfo && (
          <div style={{ fontSize: "0.68rem", color: reviseInfo.startsWith("Falha") ? "#b3261e" : "#5b6484", marginTop: "0.3rem" }}>
            {reviseInfo}
          </div>
        )}
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setVisible(PAGE_SIZE)
          }}
          placeholder={t("agent.searchPlaceholder")}
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: "0.5rem",
            padding: "0.5rem 0.75rem",
            borderRadius: "8px",
            border: "1px solid #e2e2ea",
            fontSize: "0.85rem",
            outline: "none",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.4rem" }}>
          <span style={{ fontSize: "0.68rem", fontWeight: 600, color: "#5b6484" }}>
            {t("agent.kindLabel", "Tipo")}
          </span>
          <select
            value={kindFilter}
            onChange={(e) => {
              setKindFilter(e.target.value as "ALL" | ContentKind)
              setVisible(PAGE_SIZE)
            }}
            style={{ ...agentSelectStyle, flex: 1 }}
            title={t("agent.kindTooltip", "Filtra os modelos pelo tipo de material")}
          >
            <option value="ALL">{t("kinds.all")}</option>
            {KIND_ORDER.map((kind) => (
              <option key={kind} value={kind}>
                {t(CONTENT_KINDS[kind].labelKey)}
              </option>
            ))}
          </select>
        </div>
        {categories.length > 0 && (
          <Block
            className="ar-chip-row"
            $style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: "0.35rem",
              marginTop: "0.6rem",
            }}
          >
            <CategoryChip
              active={category === "all"}
              onClick={() => {
                setCategory("all")
                setVisible(PAGE_SIZE)
              }}
            >
              {t("agent.all")}
            </CategoryChip>
            {categories.map((c) => (
              <CategoryChip
                key={c.label}
                active={category === c.label}
                onClick={() => {
                  setCategory(c.label)
                  setVisible(PAGE_SIZE)
                }}
              >
                {`${c.label} (${c.count})`}
              </CategoryChip>
            ))}
          </Block>
        )}
      </Block>

      <Scrollable>
        <div style={{ padding: "0 1.5rem 1.5rem" }}>
          {userTemplates === null ? (
            <Block $style={{ padding: "1rem 0", color: "#5b6484", fontSize: "0.85rem" }}>
              {t("agent.loadingLibrary")}
            </Block>
          ) : (
            <>
              <Block
                $style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 0 0.5rem",
                }}
              >
                <Block $style={{ color: "#5b6484", fontSize: "0.75rem" }}>
                  {t("agent.templatesCount", { count: filtered.length })}
                </Block>
                <div
                  onClick={snapshotting ? undefined : saveSnapshot}
                  title="Render the current scene and use it as this template's preview"
                  style={{
                    cursor: snapshotting ? "default" : "pointer",
                    padding: "0.2rem 0.6rem",
                    borderRadius: "999px",
                    fontSize: "0.7rem",
                    background: snapshotting ? "#e5e7eb" : "#192bc2",
                    color: snapshotting ? "#5b6484" : "#ffffff",
                  }}
                >
                  {snapshotting ? t("agent.snapshotSaving") : t("navbar.snapshot")}
                </div>
              </Block>
              {filtered.length === 0 && (
                <Block $style={{ padding: "0.75rem 0", color: "#5b6484", fontSize: "0.85rem" }}>
                  {t("agent.emptyKind", "Ainda não há modelos deste tipo.")}
                </Block>
              )}
              <div className="ar-template-grid" style={{ display: "grid", gap: "0.5rem", gridTemplateColumns: "1fr 1fr" }}>
                {filtered.slice(0, visible).map((item: any) => (
                  <ImageItem
                    key={item.id}
                    preview={thumbOf(item)}
                    lazy
                    onClick={() => openTemplate(item)}
                  />
                ))}
              </div>
              {filtered.length > visible && (
                <Block
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  $style={{
                    marginTop: "0.75rem",
                    padding: "0.5rem",
                    textAlign: "center",
                    cursor: "pointer",
                    borderRadius: "8px",
                    background: "#f8f8fb",
                    fontSize: "0.85rem",
                  }}
                >
                  {t("panels.loadMore")}
                </Block>
              )}
            </>
          )}
        </div>
      </Scrollable>
    </Block>
  )
}

function CategoryChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: "pointer",
        padding: "0.2rem 0.6rem",
        borderRadius: "999px",
        fontSize: "0.7rem",
        background: active ? "#e9f2fb" : "#f2f5fa",
        color: active ? "#192bc2" : "#5b6484",
        border: active ? "1px solid #78c0e0" : "1px solid transparent",
      }}
    >
      {children}
    </div>
  )
}

function ImageItem({ preview, onClick, lazy }: { preview: any; onClick?: (option: any) => void; lazy?: boolean }) {
  const [css] = useStyletron()
  return (
    <div
      onClick={onClick}
      className={css({
        position: "relative",
        background: "#f8f8fb",
        cursor: "pointer",
        borderRadius: "8px",
        overflow: "hidden",
        "::before:hover": {
          opacity: 1,
        },
      })}
    >
      <div
        className={css({
          backgroundImage: `linear-gradient(to bottom,
          rgba(0, 0, 0, 0) 0,
          rgba(0, 0, 0, 0.006) 8.1%,
          rgba(0, 0, 0, 0.022) 15.5%,
          rgba(0, 0, 0, 0.047) 22.5%,
          rgba(0, 0, 0, 0.079) 29%,
          rgba(0, 0, 0, 0.117) 35.3%,
          rgba(0, 0, 0, 0.158) 41.2%,
          rgba(0, 0, 0, 0.203) 47.1%,
          rgba(0, 0, 0, 0.247) 52.9%,
          rgba(0, 0, 0, 0.292) 58.8%,
          rgba(0, 0, 0, 0.333) 64.7%,
          rgba(0, 0, 0, 0.371) 71%,
          rgba(0, 0, 0, 0.403) 77.5%,
          rgba(0, 0, 0, 0.428) 84.5%,
          rgba(0, 0, 0, 0.444) 91.9%,
          rgba(0, 0, 0, 0.45) 100%)`,
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0,
          transition: "opacity 0.3s ease-in-out",
          height: "100%",
          width: "100%",
          ":hover": {
            opacity: 1,
          },
        })}
      ></div>
      <img
        src={preview}
        loading={lazy ? "lazy" : "eager"}
        className={css({
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
          verticalAlign: "middle",
        })}
      />
    </div>
  )
}
